import { types, getRoot, detach } from 'mobx-state-tree';
import { compareDesc } from 'date-fns';
import {
  fetchVideoAssignmentSubmissions,
  addVideoAssignmentSubmission,
  editVideoAssignmentSubmission,
  unsubmitVideoAssignmentSubmission as unsubmitSubmission,
  addVideoFromCloudinaryToSubmission as addVideoFromCloudinary,
  fetchByUserIdAndVideoAssignmentId,
} from 'api';
import { segmentEvent } from 'utils/segmentTrackers';
import VideoAssignmentSubmission from './models/VideoAssignmentSubmission';
import { videoAssignmentUtils } from 'utils/videoAssignmentsUtils';

export const VideoAssignmentSubmissionStore = types
  .model('VideoAssignmentSubmissionStore', {
    loading: false,
    loadError: false,
    submitting: false,
    submitSuccess: false,
    submitFailure: false,
    submissionBeingProcessed: types.frozen({
      assignmentId: '',
      isProcessing: false,
      videoId: '',
      submissionId: '',
      uploadTimestamp: 0,
    }),
    videoAssignmentSubmissions: types.maybeNull(types.array(VideoAssignmentSubmission)),
  })
  .actions((self) => ({
    afterCreate() {
      // Load submission being processed from localStorage on store creation
      const savedSubmission = videoAssignmentUtils.getSubmissionBeingProcessed();
      if (savedSubmission && savedSubmission.isProcessing) {
        self.submissionBeingProcessed = savedSubmission;
      }
    },

    async loadVideoAssignmentSubmissions() {
      self.loading = true;
      self.loadError = false;
      try {
        await self.refetchVideoAssignmentSubmission();
      } catch (error) {
        console.error(error);
        self.loadVideoAssignmentSubmissionsFailed();
      }
    },

    // Separate action: MST forbids state writes after an await inside the async action above
    loadVideoAssignmentSubmissionsFailed() {
      self.loading = false;
      self.loadError = true;
    },

    // Fetch errors are reported to Sentry by the GraphQL interceptor and rethrown so callers can
    // react. The initial load turns them into `loadError`, which the PSA tab renders as an error
    // with a retry instead of showing every PSA as not started.
    async refetchVideoAssignmentSubmission(classId) {
      const {
        classDetailsStore: { activeClass },
      } = getRoot(self);
      if (classId) {
        const data = await fetchVideoAssignmentSubmissions(classId);
        self.submittedVideoAssignmentSubmissionSuccess(data);
      } else if (activeClass && activeClass.id) {
        const data = await fetchVideoAssignmentSubmissions(activeClass.id);
        self.submittedVideoAssignmentSubmissionSuccess(data);
      } else {
        console.error('No class id found');
        self.loading = false;
      }
    },

    submittedVideoAssignmentSubmissionSuccess(data) {
      if (self.videoAssignmentSubmissions) {
        detach(self.videoAssignmentSubmissions);
      }
      self.videoAssignmentSubmissions = data;
      self.loading = false;
      self.loadError = false;
    },

    async addVideoAssignmentSubmission(classId, assignmentId) {
      try {
        await addVideoAssignmentSubmission(classId, assignmentId);
      } catch (error) {
        console.error(error);
      }
    },

    async addVideoToVideoAssignmentSubmission(submittedVideoId, submissionId) {
      try {
        await editVideoAssignmentSubmission(submittedVideoId, submissionId);
      } catch (error) {
        console.error(error);
      }
    },

    async unsubmitVideoAssignmentSubmission(submissionId) {
      // Not swallowed like the other actions so the UI can show why it was rejected.
      await unsubmitSubmission(submissionId);
    },

    async addVideoFromCloudinaryToSubmission(video, submissionId, userId) {
      try {
        await addVideoFromCloudinary(video, submissionId, userId);
      } catch (error) {
        console.error(error);
      }
    },

    setSubmissionBeingProcessed(currentVideoProcessing) {
      const processInfo = {
        ...currentVideoProcessing,
        // If uploadTimestamp is not provided, set it to current time
        uploadTimestamp: currentVideoProcessing.uploadTimestamp || Date.now(),
      };

      self.submissionBeingProcessed = processInfo;
      videoAssignmentUtils.saveSubmissionBeingProcessed(processInfo);
    },

    clearSubmissionBeingProcessed() {
      self.submissionBeingProcessed = {
        assignmentId: '',
        isProcessing: false,
        videoId: '',
        submissionId: '',
        uploadTimestamp: 0,
      };
      // Clear from localStorage
      videoAssignmentUtils.clearSubmissionBeingProcessedFromStorage();
    },

    async verifySubmissionStatus(userId, assignmentId) {
      try {
        const updatedSubmission = await fetchByUserIdAndVideoAssignmentId(userId, assignmentId);
        if (updatedSubmission.status === 'SUBMITTED') {
          return false;
        }
        return true;
      } catch (error) {
        console.error('Error verifying submission status:', error);
        throw error;
      }
    },
  }));
